import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";

const DEFAULT_ACCENT = "#e85a2a";
const DEFAULT_ACCENT_TEXT = "#ffffff";
const DEFAULT_BG = "#ffffff";
const DEFAULT_FG = "#1a1a1a";

const UbereatWidget = ({ widgetId }) => {
  const [widget, setWidget] = useState(null);
  const [isError, setIsError] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const tabRefs = useRef([]);

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/getOneWidget/${widgetId}`
        );
        if (response.status === 200) {
          const w = response.data.widget;
          if (w.isOnline === "offline") {
            setIsError(true);
          } else {
            setWidget(w);
          }
        }
      } catch (error) {
        setIsError(true);
      }
    };
    fetchWidget();
  }, [widgetId]);

  const sortedCategories = widget
    ? [...(widget.categories || [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter((c) => (c.items || []).length > 0)
    : [];

  useEffect(() => {
    if (sortedCategories.length > 0 && activeCategory >= sortedCategories.length) {
      setActiveCategory(0);
    }
  }, [sortedCategories.length, activeCategory]);

  useEffect(() => {
    const tab = tabRefs.current[activeCategory];
    if (tab && tab.scrollIntoView) {
      tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeCategory]);

  const getSortedItems = (cat) =>
    [...(cat.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const resolveProductUrl = (rawUrl) => {
    const fallback = widget?.ubereatsUrl || "https://www.ubereats.com";
    if (!rawUrl) return fallback;
    try {
      const resolved = new URL(rawUrl, "https://www.ubereats.com");
      if (resolved.protocol !== "https:") return fallback;
      return resolved.toString();
    } catch {
      return fallback;
    }
  };

  const handleOrderClick = (productUrl) => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/widgets/ubereats/recordClick/${widgetId}`)
      .catch(() => {});
    window.open(resolveProductUrl(productUrl), "_blank", "noopener,noreferrer");
  };

  const accent = widget?.btnColor || DEFAULT_ACCENT;
  const accentText = widget?.labelColor || DEFAULT_ACCENT_TEXT;
  const bg = widget?.backgroundColor || DEFAULT_BG;
  const fg = widget?.labelColor || DEFAULT_FG;
  const brd =
    widget?.borderColor && widget.borderColor !== "transparent"
      ? widget.borderColor
      : null;
  const hasBorder = !!brd;

  const widgetStyles = useMemo(() => `
    .uw-tab {
      position: relative;
      transition: color 0.2s ease, transform 0.15s ease;
    }
    .uw-tab::after {
      content: "";
      position: absolute;
      left: 12px; right: 12px; bottom: -1px;
      height: 2.5px;
      background: transparent;
      border-radius: 2px 2px 0 0;
      transition: background 0.25s ease;
    }
    .uw-tab.active::after {
      background: linear-gradient(to right, ${accent}, ${accent}dd);
      box-shadow: 0 -1px 8px ${accent}55;
    }
    .uw-tab:not(.active):hover { color: #1a1a1a; transform: translateY(-1px); }
    .uw-tab:not(.active):hover::after { background: ${accent}30; }
    .uw-tab-count {
      transition: background-color 0.2s ease, color 0.2s ease;
    }
    @keyframes uw-fade-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .uw-section { animation: uw-fade-in 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
    .uw-card {
      transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
                  box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1),
                  border-color 0.25s ease;
      cursor: pointer;
    }
    .uw-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 30px rgba(0,0,0,0.08);
      border-color: ${accent}55;
    }
    .uw-card:active { transform: translateY(-1px); }
    .uw-card-img {
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .uw-card:hover .uw-card-img { transform: scale(1.06); }
    .uw-cta {
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
                  box-shadow 0.2s ease;
      box-shadow: 0 2px 8px ${accent}40;
    }
    .uw-card:hover .uw-cta {
      transform: scale(1.12) translateX(2px);
      box-shadow: 0 4px 14px ${accent}66;
    }
    .uw-cta:active { transform: scale(0.94); }
    .uw-tabs-wrap {
      position: relative;
    }
    .uw-tabs-wrap::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 1px;
      width: 32px;
      pointer-events: none;
      background: linear-gradient(to right, transparent, ${bg} 80%);
    }
    .uw-tabs-scroll {
      scroll-snap-type: x proximity;
      -webkit-overflow-scrolling: touch;
    }
    .uw-tabs-scroll > button {
      scroll-snap-align: center;
    }
    .uw-tabs-scroll::-webkit-scrollbar { height: 0; width: 0; }
    .uw-line {
      flex: 1;
      height: 1px;
      background: linear-gradient(to right, transparent, ${accent}40, transparent);
    }
  `, [accent, bg]);

  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-neutral-950 text-white">
        <div className="text-center p-8">
          <p className="text-xl font-semibold mb-2">Widget désactivé</p>
          <p className="text-sm text-neutral-400">
            Contactez{" "}
            <a href="https://local.fr" className="underline hover:text-white">
              Local.fr
            </a>{" "}
            pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  if (!widget) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-800 rounded-full animate-spin" />
      </div>
    );
  }

  const activeCat = sortedCategories[activeCategory];
  const items = activeCat ? getSortedItems(activeCat) : [];

  return (
    <>
      <style>{widgetStyles}</style>
      <div
        className="flex flex-col w-full sm:rounded-2xl overflow-hidden font-sans"
        style={{
          backgroundColor: bg,
          boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
          border: hasBorder ? `2px solid ${brd}` : "1px solid #ececec",
        }}
      >
        {/* Mini header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="min-w-0">
            <p className="text-[16px] font-bold truncate" style={{ color: fg }}>
              {widget.title}
            </p>
            {widget.storeName && (
              <p className="text-[11px] truncate mt-0.5" style={{ color: fg, opacity: 0.5 }}>
                Menu Uber Eats · {widget.storeName}
              </p>
            )}
          </div>
          {widget.contactPhone && (
            <a
              href={`tel:${widget.contactPhone}`}
              className="shrink-0 ml-3 w-9 h-9 rounded-full flex items-center justify-center text-xs transition-colors"
              style={{ backgroundColor: `${fg}10`, color: fg }}
            >
              <FontAwesomeIcon icon={faPhone} />
            </a>
          )}
        </div>

        {/* Top nav — text tabs */}
        <div className="uw-tabs-wrap border-b" style={{ borderColor: `${fg}10` }}>
          <div className="uw-tabs-scroll flex gap-1 overflow-x-auto px-4">
            {sortedCategories.map((cat, idx) => {
              const isActive = activeCategory === idx;
              const count = (cat.items || []).length;
              return (
                <button
                  ref={(el) => (tabRefs.current[idx] = el)}
                  key={cat.name + "_" + idx}
                  type="button"
                  onClick={() => setActiveCategory(idx)}
                  className={`uw-tab shrink-0 flex items-center gap-1.5 px-3 pt-3.5 pb-3 text-[13px] font-semibold whitespace-nowrap ${
                    isActive ? "active" : ""
                  }`}
                  style={{ color: isActive ? accent : `${fg}60` }}
                >
                  <span>{cat.name}</span>
                  <span
                    className="uw-tab-count text-[10px] font-bold px-1.5 py-px rounded-full leading-tight"
                    style={{
                      backgroundColor: isActive ? `${accent}1a` : `${fg}10`,
                      color: isActive ? accent : `${fg}80`,
                      minWidth: "18px",
                      textAlign: "center",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeCat && (
            <div key={activeCategory} className="uw-section">
              {/* Section header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="uw-line" />
                <h2 className="text-[15px] font-bold whitespace-nowrap tracking-tight" style={{ color: fg }}>
                  {activeCat.name}
                </h2>
                <div className="uw-line" />
              </div>

              {/* Products grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOrderClick(item.productUrl)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOrderClick(item.productUrl);
                      }
                    }}
                    className="uw-card flex gap-4 p-4 rounded-2xl bg-white border border-neutral-150"
                    style={{ borderColor: "#eeeeee" }}
                  >
                    {/* Image */}
                    <div className="w-[88px] h-[88px] rounded-2xl overflow-hidden shrink-0 bg-neutral-50">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt=""
                          className="uw-card-img w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-neutral-300 select-none">
                          {(item.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 flex flex-col">
                      <h3 className="text-left text-[14px] font-bold text-neutral-900 leading-snug line-clamp-1">
                        {item.name}
                      </h3>
                      {item.description && (
                        <p className="text-left text-[12px] text-neutral-500 leading-relaxed mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <p className="text-[15px] font-extrabold text-neutral-900 tracking-tight">
                          {Number(item.price).toFixed(2).replace(".", ",")}
                          <span className="text-[12px] font-bold text-neutral-400 ml-0.5">€</span>
                        </p>
                        <span
                          aria-hidden="true"
                          className="uw-cta inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0"
                          style={{
                            backgroundColor: accent,
                            color: accentText,
                          }}
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UbereatWidget;
